/**
 * HO DPR Sync — real-time Daily Production Report across ALL factories.
 * Visible to super_admin / admin / executive_admin. Auto-refreshes every 60s.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Factory, PackageCheck, PlayCircle, AlertOctagon, RefreshCw, Users } from "lucide-react";
import { fmtDate } from "@/lib/utils-format";
import ManufacturingOpsPanel from "@/components/mfg/ManufacturingOpsPanel";

export default function HODprSyncPage() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/production/dpr", { params: { date_: date } });
      setData(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);
  useEffect(() => { const t = setInterval(load, 60_000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [date]);

  return (
    <div className="space-y-6 p-6" data-testid="ho-dpr-sync-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="uc-label">Manufacturing · HO Sync</div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1 flex items-center gap-2">
            <Factory className="w-8 h-8 text-[#C05621]" /> Daily Production Report — All Factories
          </h1>
          <p className="text-sm text-[#8B7355] mt-1">Real-time roll-up of every factory's shop-floor. Auto-refreshes every 60 seconds.</p>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs">Date</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40" data-testid="ho-dpr-date" />
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="ho-dpr-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="ho-dpr-grand">
            <Kpi label="Factories" value={data.factory_count} tone="orange" />
            <Kpi label="Batches Produced" value={data.grand_totals.batches_produced} tone="blue" />
            <Kpi label="FG Units" value={data.grand_totals.fg_units_total} tone="green" icon={PackageCheck} />
            <Kpi label="Labour Hours" value={data.grand_totals.labour_hours} tone="amber" />
            <Kpi label="OT Hours" value={data.grand_totals.ot_hours} tone="purple" />
            <Kpi label="Open WIP" value={data.grand_totals.wip_open} tone="amber" icon={PlayCircle} />
            <Kpi label="Delays" value={`${data.grand_totals.delays_count} · ${data.grand_totals.delay_minutes}m`} tone={data.grand_totals.delays_count > 0 ? "red" : "slate"} icon={AlertOctagon} />
          </div>

          <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#FAF6EE] text-[#8B7355] text-xs uppercase">
                <tr>
                  <th className="text-left px-3 py-2">Factory</th>
                  <th className="text-right px-3 py-2">Batches</th>
                  <th className="text-right px-3 py-2">FG Units</th>
                  <th className="text-right px-3 py-2">Raw Lines</th>
                  <th className="text-right px-3 py-2">Labour (h)</th>
                  <th className="text-right px-3 py-2">OT (h)</th>
                  <th className="text-right px-3 py-2">WIP</th>
                  <th className="text-right px-3 py-2">Delays</th>
                  <th className="text-left px-3 py-2 font-mono">FactoryUnitID</th>
                  <th></th>
                </tr>
              </thead>
              <tbody data-testid="ho-dpr-rows">
                {data.factories.map((f) => (
                  <tr key={f.factory_id} className="border-t border-[#EAE5D9]">
                    <td className="px-3 py-2 font-medium">{f.factory_name}</td>
                    <td className="px-3 py-2 text-right">{f.totals.batches_produced}</td>
                    <td className="px-3 py-2 text-right font-semibold text-emerald-700">{f.totals.fg_units_total}</td>
                    <td className="px-3 py-2 text-right">{f.totals.raw_material_lines}</td>
                    <td className="px-3 py-2 text-right">{f.totals.labour_hours}</td>
                    <td className="px-3 py-2 text-right">{f.totals.ot_hours}</td>
                    <td className="px-3 py-2 text-right"><Badge className={`border-0 ${f.totals.wip_open > 0 ? "bg-amber-100 text-amber-800" : "bg-stone-100 text-stone-700"}`}>{f.totals.wip_open}</Badge></td>
                    <td className="px-3 py-2 text-right">
                      {f.totals.delays_count > 0 ? <Badge className="bg-red-100 text-red-800 border-0">{f.totals.delays_count} · {f.totals.delay_minutes}m</Badge> : <span className="text-[#8B7355]">—</span>}
                    </td>
                    <td className="px-3 py-2 font-mono text-[10px] text-[#8B7355]">{f.factory_id.slice(0, 12)}…</td>
                    <td className="px-3 py-2 text-right"><Button size="sm" variant="outline" onClick={() => setExpanded(expanded === f.factory_id ? null : f.factory_id)} data-testid={`ho-dpr-drill-${f.factory_id}`}>{expanded === f.factory_id ? "Hide" : "Drill-down"}</Button></td>
                  </tr>
                ))}
                {data.factories.length === 0 && <tr><td colSpan={10} className="px-3 py-8 text-center text-[#8B7355]">No factory data for {fmtDate(date)}.</td></tr>}
              </tbody>
            </table>
          </div>

          {expanded && (
            <div className="border border-[#EAE5D9] rounded-lg p-4 bg-white" data-testid="ho-dpr-drilldown">
              <ManufacturingOpsPanel branchId={expanded} branchName={data.factories.find((x) => x.factory_id === expanded)?.factory_name} allowLogging={false} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, icon: Icon, tone }) {
  const map = {
    green: "text-emerald-700 border-emerald-200 bg-emerald-50",
    red: "text-red-700 border-red-200 bg-red-50",
    amber: "text-amber-700 border-amber-200 bg-amber-50",
    blue: "text-blue-700 border-blue-200 bg-blue-50",
    purple: "text-purple-700 border-purple-200 bg-purple-50",
    orange: "text-[#C05621] border-orange-200 bg-orange-50",
    slate: "text-stone-700 border-stone-200 bg-stone-50",
  };
  return (
    <div className={`border rounded-md p-3 ${map[tone] || map.slate}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider opacity-70">{label}</span>
        {Icon && <Icon className="w-3.5 h-3.5 opacity-70" />}
      </div>
      <div className="text-2xl font-[Outfit] font-semibold mt-1">{value ?? 0}</div>
    </div>
  );
}
